import { Builder } from '@builder.io/react';
import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import getAssetSource from '../../../app/Search/utils/getAssetSource';
import { fetchBuilderDataFromModel } from '../../../common/utils';
import { ProgressiveImage } from '../../../storywind';
import { Button } from '../../storyblocks-design-system/components/atoms/Button/Button';
import { Text } from '../../storyblocks-design-system/components/atoms/Text/Text';
import withBuilderDefaults, {
  keepCompleteItems,
} from '../../utils/withBuilderDefaults';

interface BrandsAndCreatorsSectionProps {
  urlSlug?: string;
  modelName?: string;
}

const BUILDER_ASSET_BASE =
  'https://cdn.builder.io/api/v1/image/assets%2Fe7fb3d1e4da14573bd2a1edb7bfee5f1%2F';

export interface BrandsAndCreatorsBuilderData {
  heading: string;
  logos: { alt: string; image: string }[];
  creators: { username: string; alt: string; image: string }[];
  cta: {
    buttonText: string;
    buttonHref: string;
  };
}
const BRANDS_AND_CREATORS_DEFAULTS: BrandsAndCreatorsBuilderData = {
  heading: 'Loved by 10,000+ brands and creators',
  logos: [
    {
      alt: 'Canon logo',
      image: `${BUILDER_ASSET_BASE}e2f3e27a524e487898429ea129417a8c`,
    },
    {
      alt: 'Univision logo',
      image: `${BUILDER_ASSET_BASE}62c39b57ffb14535b47a71fff55e2894`,
    },
    {
      alt: 'iHeartMedia logo',
      image: `${BUILDER_ASSET_BASE}1251798c82a64ac3be33b75fe76ff95f`,
    },
    {
      alt: 'Live Nation logo',
      image: `${BUILDER_ASSET_BASE}b3ce0175e50c4c9993a6d9692a1a465a`,
    },
    {
      alt: 'BCG logo',
      image: `${BUILDER_ASSET_BASE}3030549544a0467e87a6ddde01ec4d46`,
    },
    {
      alt: 'ASU logo',
      image: `${BUILDER_ASSET_BASE}89b6e085e0e143cfaacbb75377ba0a27`,
    },
    {
      alt: 'CBS logo',
      image: `${BUILDER_ASSET_BASE}e97c5377fd8746bc832274f79bda0240`,
    },
    {
      alt: 'Mayo Clinic logo',
      image: `${BUILDER_ASSET_BASE}7a1a31899fed46ae87d358d11d052dac`,
    },
    {
      alt: 'HarperCollins logo',
      image: `${BUILDER_ASSET_BASE}3ab9ddffe718418684d09104eadbceda`,
    },
  ],
  creators: [
    {
      username: '@PremiereBasics',
      alt: 'PremiereBasics',
      image: `${BUILDER_ASSET_BASE}6135f219cee54ec0a6ee09fa69ec1345`,
    },
    {
      username: '@katie',
      alt: 'katie',
      image: `${BUILDER_ASSET_BASE}c3b5362fd982482c9cc1d320890af485`,
    },
    {
      username: '@Daniel_Batal',
      alt: 'Daniel Batal',
      image: `${BUILDER_ASSET_BASE}c4429183cd6b423eb48c37a696a66f3e`,
    },
    {
      username: '@JadeBeason',
      alt: 'Jade Beason',
      image: `${BUILDER_ASSET_BASE}69f5811ea8c842ae88e63fb591d32749`,
    },
    {
      username: '@froknowsphoto',
      alt: 'froknowsphoto',
      image: `${BUILDER_ASSET_BASE}f30847f01fcc421893d3f7b6dd22b4d6`,
    },
  ],
  cta: { buttonText: 'Get Started Today', buttonHref: '/login' },
};

const BrandsAndCreatorsSection = ({
  urlSlug,
  modelName = 'landing-search-page',
}: BrandsAndCreatorsSectionProps) => {
  const [builderData, setBuilderData] =
    useState<BrandsAndCreatorsBuilderData>(null); // From builder

  useEffect(() => {
    const builderOptions = {
      query: {
        'data.urlSlug': urlSlug,
      },
      options: {
        fields: 'data',
      },
    };
    const fetchBuilderData = async () => {
      const data = await fetchBuilderDataFromModel(modelName, builderOptions);
      if (data && data.brandsAndCreators) {
        const builderData: BrandsAndCreatorsBuilderData = {
          heading: data.brandsAndCreators.heading,
          logos: data.brandsAndCreators.logos,
          creators: data.brandsAndCreators.creators,
          cta: data.brandsAndCreators.cta,
        };
        setBuilderData(builderData);
      }
    };
    if (urlSlug) {
      fetchBuilderData();
    }
  }, [urlSlug, modelName]);

  const { heading, logos, creators, cta } = withBuilderDefaults(
    {
      ...builderData,
      logos: keepCompleteItems(builderData?.logos, ['image', 'alt']),
      creators: keepCompleteItems(builderData?.creators, ['username', 'image']),
    },
    BRANDS_AND_CREATORS_DEFAULTS
  );

  return (
    <section className="flex flex-col items-center w-full gap-8 px-6 py-16  md:px-12">
      <Text
        fontFamily="polysans"
        fontWeight="semibold"
        fontSize="4xl"
        className="text-center max-w-2xl"
        asChild
      >
        <h2>{heading}</h2>
      </Text>
      <BrandLogosRow logos={logos} />
      <CreatorsRow creators={creators} />
      <Button variant="primary" size="large" href={cta.buttonHref}>
        {cta.buttonText}
      </Button>
    </section>
  );
};

const BrandLogosRow = ({
  logos,
}: {
  logos: { image: string; alt: string }[];
}) => (
  <ul
    className="list-none p-0 m-0 flex flex-wrap justify-center lg:grid gap-y-4 w-full items-center grayscale mx-auto"
    style={{
      maxWidth: '1200px',
      gridTemplateColumns: `repeat(${logos.length}, minmax(0, 1fr))`,
    }}
  >
    {logos.map(({ image, alt }, index) => (
      <li
        key={`brandAndCreatorSection_${alt}_${index}`}
        className="flex items-center justify-center w-1/3 md:w-1/5 lg:w-auto p-0.5"
        style={{ height: '59px' }}
      >
        <ProgressiveImage
          className="max-h-full max-w-full object-contain"
          src={getAssetSource(image)}
          alt={alt}
          shouldLazyLoad
        />
      </li>
    ))}
  </ul>
);

const CreatorsRow = ({
  creators,
}: {
  creators: { image: string; username: string; alt: string }[];
}) => (
  <div className="w-full mx-auto" style={{ maxWidth: '1440px' }}>
    <ul className="list-none p-0 -mx-2 -mb-4 flex flex-wrap justify-center">
      {creators.map(({ image, username, alt }) => (
        <li
          key={username}
          className="flex flex-col gap-2 w-full md:w-1/2 lg:w-1/5 px-2 pb-4"
        >
          <div
            className={twMerge(
              'relative overflow-hidden rounded-xl bg-gray-800'
            )}
            style={{ aspectRatio: '1 / 1' }}
          >
            <ProgressiveImage
              className="absolute inset-0 w-full h-full object-cover"
              src={getAssetSource(image)}
              alt={alt}
              shouldLazyLoad
            />
          </div>
          <Text
            fontFamily="polysans"
            fontWeight="semibold"
            fontSize="base"
            className="text-left"
          >
            <span>{username}</span>
          </Text>
        </li>
      ))}
    </ul>
  </div>
);

export default BrandsAndCreatorsSection;
